import React, { useContext, useEffect, useRef, useState } from 'react';
import { Grid } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import DirectionsCar from '@material-ui/icons/DirectionsCar';
import Save from '@material-ui/icons/Save';
import { Button, Container, FeedBackNotification, LoadingBounce, Panel, useDialogs, usePhoneView } from '@michelin/acid-components';
import { maestroApiCall, useSnackbar, useTranslation } from '@michelin/central-provider';
import { UFOVehicleDetailsForm, UFOVehicleDetailsFormProvider } from '@michelin/core-profile-ui-tools';
import { FormHandles } from '@michelin/core-profile-ui-tools/build/components/Form/Form';
import { UFOVehicleDetailsFormData } from '@michelin/core-profile-ui-tools/build/components/UFOVehicleDetailsForm/types';
import { CountryCode } from '@michelin/core-profile-ui-tools/build/types';
import { PlainMessage } from 'components/PlainMessage/PlainMessage';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { saveVehicleAPICall } from 'compositions/Wizard/SelectVehicle/Utils';
import {
  useGetCustomAspectRatios,
  useGetCustomDiameters,
  useGetCustomLoads,
  useGetCustomSizes,
  useGetCustomSpeedRatings,
} from 'compositions/Wizard/SelectVehicle/customTireSize';
import {
  useGetMakes,
  useGetModels,
  useGetOptions,
  useGetStates,
  useGetTireSizes,
  useGetYears,
} from 'compositions/Wizard/SelectVehicle/vehiclesInformation';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { useQuery } from 'react-query';
import { vehiclesDetailAPIType } from 'types/APITypes';
import { VehicleType } from 'types/UFOTypes';

interface VehiclesDetailProps {
  action?: 'edit' | 'view' | 'add';
  vehicleId?: string;
  fleetId: string;
  providerId: string;
  setVehicle: (v: VehicleType) => void;
  onDiscard: () => void;
  wizardMode?: boolean;
}

function VehiclesDetail(props: VehiclesDetailProps) {
  const { t } = useTranslation();
  const mobileView = usePhoneView();
  const ref = useRef<FormHandles>(null);

  const { action, fleetId, vehicleId, setVehicle, onDiscard, providerId, wizardMode } = props;

  const getYears = useGetYears();
  const getMakes = useGetMakes();
  const getModels = useGetModels();
  const getVehicleOptions = useGetOptions();
  const getTireSizes = useGetTireSizes();
  const getStates = useGetStates();
  const getCustomSizes = useGetCustomSizes();
  const getCustomAspectRatios = useGetCustomAspectRatios();
  const getCustomDiameters = useGetCustomDiameters();
  const getCustomLoads = useGetCustomLoads();
  const getCustomSpeedRatings = useGetCustomSpeedRatings();

  const noti = useSnackbar();

  const { setVehicleIsEdited } = useContext(OrderDetailContext);

  // Detail
  const [originalData, setOriginalData] = useState<VehicleType>({} as VehicleType);
  const [loading, setLoading] = useState<boolean>(action !== 'add');
  const [modified, setModified] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { errorDialog, confirmDialog } = useDialogs();

  const vehiclesDetail = useQuery<VehicleType | null, Error>(
    ['get-vehicles-detail', fleetId, vehicleId],
    () => {
      if (!fleetId || !vehicleId) return null;
      return maestroApiCall(vehiclesDetailAPIType(fleetId, vehicleId));
    },
    {
      cacheTime: 3600,
    },
  );

  useEffect(() => {
    async function fetchData() {
      if (vehicleId && !vehiclesDetail.isLoading && vehiclesDetail.data) {
        if (vehiclesDetail.error) {
          const notiKey = noti.enqueueSnackbar(
            <FeedBackNotification
              onUpdate={() => noti.closeSnackbar(notiKey ?? undefined)}
              notification={{ message: { subject: t(vehiclesDetail.error.message) } }}
              color="danger"
              icon={<DirectionsCar />}
            />,
            {
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              },

              ContentProps: {
                style: {
                  background: 'transparent',
                  boxShadow: 'none',
                },
              },
            },
          );
          return;
        }
        const data: VehicleType = vehiclesDetail?.data;
        if (data) {
          data.tire_size_id = data.tire_size_id ?? 'custom';
          data.model_id = data.model_id ?? 'unknown';
          data.make_id = data.make_id ?? 'unknown';
        }
        setOriginalData(data);
        setLoading(false);
      }
    }

    fetchData().catch((_) => {
      console.error('An error occurred while managing vehiclesDetail data');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesDetail]);

  /* ************************** */
  /* ************************** */
  /* Data Input and Validation  */
  /* ************************** */
  /* ************************** */

  function showValidationErrors(
    errors: FieldErrors<UFOVehicleDetailsFormData> = {} as FieldErrors<UFOVehicleDetailsFormData>,
  ) {
    const msg: string[] = [];

    if (errors.option_id) msg.push(`• ${t('Option is required')}`);
    if (errors.tire_size_id) msg.push(`• ${t('Tire Size is required')}`);
    if (errors.year) msg.push(`• ${t('Year is required')}`);
    if (errors.make_id) msg.push(`• ${t('Make is required')}`);
    if (errors.model_id) msg.push(`• ${t('Model is required')}`);
    if (errors.fleet_id_no) msg.push(`• ${t('Vehicle Unit Number is required')}`);
    if (errors.vin) msg.push(`• ${t('VIN is required and should be 17 characters long')}`);
    if (errors.license_plate) msg.push(`• ${t('License Plate is required and should be 7 characters or shorter')}`);
    if (errors.license_state_abbreviation) msg.push(`• ${t('License Plate State is required')}`);

    if (errors.tire_sw) msg.push(`• ${t('Custom tire size is required')}`);
    if (errors.tire_aspect) msg.push(`• ${t('Custom tire size aspect ratio is required')}`);
    if (errors.tire_rim) msg.push(`• ${t('Custom tire size diameter is required')}`);
    if (errors.load_index) msg.push(`• ${t('Custom tire size load is required')}`);
    if (errors.speed_rating) msg.push(`• ${t('Custom tire size speed rating is required')}`);

    if (msg.length > 0) {
      msg.unshift(t('The following occurred before saving:'), ' ');
      errorDialog(msg, t('Missing required fields'), t('Ok'))?.catch((_) => {
        // TODO: better error handling
        console.error('An error occurred in the error dialog');
      });
      // scrollToDisplayFirstError();
    }
  }

  async function saveVehicle(vehicleData: VehicleType) {
    setSaving(true);
    if (fleetId) {
      const response = await saveVehicleAPICall(
        { ...vehicleData, type: 'urban' },
        fleetId,
        providerId,
        vehicleId,
      );
      if (!response || response.status !== 200) {
        errorDialog(
          [t(response.body.error) || t('Unhandled error')],
          t('Error while saving vehicle'),
          t('Ok'),
          undefined,
          {
            disableBackdropClick: true,
          },
        )?.catch((_) => {
          // TODO: better error handling
          console.error('An error occurred in the error dialog');
        });
      } else if (response.body?.error) {
        errorDialog(t(response.body.error), t('Error while saving vehicle'), t('Ok'), undefined, {
          disableBackdropClick: true,
        })?.catch((_) => {
          console.error('An error occurred in the error dialog');
        });
      } else {
        const notiKey = noti.enqueueSnackbar(
          <FeedBackNotification
            onUpdate={() => noti.closeSnackbar(notiKey ?? undefined)}
            notification={{ message: { subject: t('Successfully saved vehicle !') } }}
            color="success"
            icon={<DirectionsCar />}
          />,
          {
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },

            ContentProps: {
              style: {
                background: 'transparent',
                boxShadow: 'none',
              },
            },
          },
        );
        setModified(false);
        // TODO: fix typings
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setOriginalData(response.body);
        // TODO: fix typings
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setVehicle(response.body);
      }
    } else {
      showValidationErrors();
    }
    setSaving(false);
  }

  const cancelEdition = async (mode: 'ask' | 'stay' | 'discard') => {
    switch (mode) {
      case 'ask':
        confirmDialog(
          t('Are you sure you want to discard all the changes you have made?'),
          t('Discard Changes'),
          t('Yes'),
          t('No'),
          () => {
            cancelEdition('discard').catch((_) => {
              // TODO: better error handling
              console.error('An error occurred while cancelling the order edit');
            });
          },
          () => {},
        )?.catch((_) => {
          // TODO: better error handling
          console.error('An error occurred in the confirm dialog');
        });
        break;
      case 'discard':
        setModified(false);
        if (ref.current) ref.current.reset();
        onDiscard();
        break;
      default:
        break;
    }
  };

  if (loading) return <LoadingBounce style={{ height: '70vh' }} />;

  if (vehiclesDetail.error) {
    return (
      <Container>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12}>
            <PlainMessage
              title={t('API Error')}
              messages={[
                t('Application could not load vehicles data.'),
                t('Please try again later or contact support if the error persists.'),
              ]}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Panel spacing={0}>
          <>
            <Container spacing={mobileView ? 0 : undefined}>
              <UFOVehicleDetailsFormProvider
                countryCode={CountryCode.US}
                fetchYears={getYears}
                fetchMakes={getMakes}
                fetchModels={getModels}
                fetchOptions={getVehicleOptions}
                fetchTireSizes={getTireSizes}
                fetchStates={getStates}
                fetchCustomTireSizeSizes={getCustomSizes}
                fetchCustomTireSizeAspectRatios={getCustomAspectRatios}
                fetchCustomTireSizeDiameters={getCustomDiameters}
                fetchCustomTireSizeLoads={getCustomLoads}
                fetchCustomTireSizeSpeedRatings={getCustomSpeedRatings}
              >
                <UFOVehicleDetailsForm
                  optionalTireSizes
                  hideDetails={action !== 'add'}
                  ref={ref}
                  readOnly={action === 'view'}
                  mode={action}
                  onSubmit={async (data) => {
                    const newData = data as VehicleType;
                    newData.vin = data.vin ?? originalData.vin;
                    if (action === 'edit') {
                      if (!wizardMode) setVehicle(newData);
                      if (wizardMode) {
                        saveVehicle(newData).catch((_) => {
                          // TODO: better error handling
                          console.error('An error occurred while saving the vehicle');
                        });
                      }
                      if (setVehicleIsEdited) setVehicleIsEdited(true);
                    }
                    if (action === 'add') {
                      saveVehicle(newData).catch((_) => {
                        // TODO: better error handling
                        console.error('An error occurred while saving the vehicle');
                      });
                    }
                  }}
                  onDirtyStateChanged={(dirtyState: boolean) => {
                    setModified(modified || dirtyState);
                  }}
                  // eslint-disable-next-line react/jsx-no-bind
                  onValidationFailed={showValidationErrors}
                  /* eslint-disable-next-line max-len */
                  defaultValues={{
                    ...originalData,

                    tire_size_id: action !== 'add' ? originalData.tire_size_id || 'custom' : '',
                    speed_rating: originalData.custom_tire_size_id,
                  }}
                />
              </UFOVehicleDetailsFormProvider>
            </Container>
          </>
          <div style={{ width: '100%', textAlign: 'right' }}>
            <Button
              style={{ margin: 8, width: 100 }}
              variant="contained"
              size={mobileView ? 'small' : undefined}
              color={modified ? 'primary' : 'secondary'}
              startIcon={saving ? undefined : <Save />}
              onClick={
                modified
                  ? () => {
                      if (ref?.current) ref.current.submit();
                    }
                  : undefined
              }
              disabled={saving}
            >
              {!saving && t('Save')}
              {saving && <LoadingBounce size="0.7vh" />}
            </Button>
            <Button
              variant="contained"
              style={{ margin: 8, width: 100 }}
              size={mobileView ? 'small' : undefined}
              startIcon={<Close />}
              color="secondary"
              onClick={() => {
                if (!modified) {
                  cancelEdition('discard').catch((_) => {
                    // TODO: better error handling
                    console.error('An error occurred while cancelling the order edit');
                  });
                } else {
                  cancelEdition('ask').catch((_) => {
                    // TODO: better error handling
                    console.error('An error occurred while cancelling the order edit');
                  });
                }
              }}
              disabled={saving}
            >
              {t('Discard')}
            </Button>
          </div>
        </Panel>
      </Container>
    </>
  );
}

export default VehiclesDetail;
